/**
 * Contract Types admin section — CR-1441.
 *
 * Per-company configurable contract types (name + 3-letter abbreviation +
 * enabled). The abbreviation is the prefix used to auto-generate contract
 * numbers when adding a contract (e.g. CON -> CON000001). Mirrors the
 * RFQTypesSection pattern.
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { Loader2, Plus, Pencil, Trash2, FileText } from 'lucide-react';
import { purchasingAPI, fetchAllPages } from '../../../lib/api';
import type { ContractType } from '../../../lib/api';

/**
 * Configurable contract (price book) types.
 *
 * @param embedded - CR-7321: when true, render without the outer Card so the
 *   parent "Price Book Types & Sharing" section can host the types table and
 *   the sharing controls under one card. Standalone (false) keeps its own card.
 */
export function ContractTypesSection({ embedded = false }: { embedded?: boolean } = {}) {
  const [types, setTypes] = useState<ContractType[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ContractType | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', abbreviation: '', enabled: true });
  const [deleteTarget, setDeleteTarget] = useState<ContractType | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Walk pages — the backend clamps page_size, so one request silently truncated.
      const list = await fetchAllPages<ContractType>((page) => purchasingAPI.getContractTypes({ page_size: 100, page }));
      setTypes(list);
    } catch (err) {
      console.error('Failed to load contract types', err);
      toast.error('Failed to load contract types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDialog = (ct?: ContractType) => {
    if (ct) {
      setEditing(ct);
      setForm({ name: ct.name, abbreviation: ct.abbreviation, enabled: ct.enabled });
    } else {
      setEditing(null);
      setForm({ name: '', abbreviation: '', enabled: true });
    }
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.abbreviation.trim()) { toast.error('Abbreviation is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, abbreviation: form.abbreviation.toUpperCase() };
      if (editing) {
        await purchasingAPI.updateContractType(editing.id, payload);
        toast.success('Contract type updated');
      } else {
        await purchasingAPI.createContractType(payload);
        toast.success('Contract type created');
      }
      setDialogOpen(false);
      await fetchData();
    } catch (err: unknown) {
      console.error('Failed to save contract type', err);
      // Surface the backend's field-level message (duplicate name/abbrev) —
      // the platform wraps errors as {details: {fields: {...}}}.
      const data = (err as { response?: { data?: Record<string, unknown> } }).response?.data;
      const fields = (data?.['details'] as { fields?: Record<string, string[]> } | undefined)?.fields;
      const firstField = fields && Object.values(fields)[0];
      const msg = (Array.isArray(firstField) ? firstField[0] : undefined)
        ?? (data?.['detail'] as string | undefined)
        ?? (err instanceof Error ? err.message : 'Failed to save contract type');
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await purchasingAPI.deleteContractType(deleteTarget.id);
      toast.success('Contract type deleted');
      setDeleteTarget(null);
      await fetchData();
    } catch (err: unknown) {
      console.error('Failed to delete contract type', err);
      // PROTECT FK — a type with contracts attached can't be deleted (409).
      toast.error('Cannot delete — this contract type still has contracts attached. Disable it instead.');
    }
  };

  const toggleEnabled = async (ct: ContractType) => {
    setTypes((prev) => prev.map((t) => (t.id === ct.id ? { ...t, enabled: !t.enabled } : t)));
    try {
      await purchasingAPI.updateContractType(ct.id, { enabled: !ct.enabled });
    } catch (err) {
      console.error('Failed to toggle', err);
      toast.error('Failed to update contract type');
      await fetchData();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const header = (
    <div className="flex items-center justify-between">
      <div>
        {embedded ? (
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <FileText className="h-5 w-5" />
            Types
          </h3>
        ) : (
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Manage Contract Types
          </CardTitle>
        )}
        <p className="text-sm text-muted-foreground mt-1">
          Each type's 3-letter abbreviation becomes the prefix of the auto-generated contract
          number when adding a contract (e.g. CON &rarr; CON000001).
        </p>
      </div>
      <Button size="sm" onClick={() => openDialog()}>
        <Plus className="h-4 w-4 mr-1" />
        Add Contract Type
      </Button>
    </div>
  );

  const body = types.length === 0 ? (
    <div className="text-center py-8 text-muted-foreground">
      <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
      <p>No contract types yet</p>
      <p className="text-sm mt-1">Create one so contracts can be added with auto-generated numbers.</p>
    </div>
  ) : (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead className="w-32">Prefix</TableHead>
          <TableHead className="w-32 text-center">Status</TableHead>
          <TableHead className="w-32 text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {types.map((t) => (
          <TableRow key={t.id}>
            <TableCell className="font-medium">{t.name}</TableCell>
            <TableCell>
              <span className="font-mono text-sm bg-muted px-2 py-0.5 rounded">{t.abbreviation}</span>
            </TableCell>
            <TableCell className="text-center">
              <div className="flex items-center justify-center gap-2">
                <Switch checked={t.enabled} onCheckedChange={() => toggleEnabled(t)} />
                <span className="text-xs text-muted-foreground">
                  {t.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </TableCell>
            <TableCell className="text-right">
              <Button variant="ghost" size="sm" onClick={() => openDialog(t)}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(t)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <>
      {embedded ? (
        <div className="space-y-4">
          {header}
          {body}
        </div>
      ) : (
        <Card>
          <CardHeader>{header}</CardHeader>
          <CardContent>{body}</CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Contract Type' : 'New Contract Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="contracttype-name">Name</Label>
              <Input
                id="contracttype-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Contract, Standard, Accounting"
              />
            </div>
            <div>
              <Label htmlFor="contracttype-abbr">Abbreviation (3-letter prefix)</Label>
              <Input
                id="contracttype-abbr"
                value={form.abbreviation}
                maxLength={3}
                onChange={(e) => setForm({ ...form, abbreviation: e.target.value.toUpperCase().slice(0, 3) })}
                placeholder="CON"
                className="font-mono uppercase"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="contracttype-enabled"
                checked={form.enabled}
                onCheckedChange={(checked) => setForm({ ...form, enabled: checked })}
              />
              <Label htmlFor="contracttype-enabled">Enabled</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Contract Type?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            Delete &ldquo;{deleteTarget?.name}&rdquo;? If any contract uses it the delete is rejected —
            disable it instead to hide it from the Add Contract picker.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
